import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { IPagedCollection } from '../../extViewmodels';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { ISupportNotification } from '../../models/Support/ISupportNotification';
import { supportPollingRetry, supportQueryRetry } from '../../utils/Support/supportHelpers';
import { SUPPORT_PATHS, SUPPORT_QUERY_KEYS, type SupportNotificationFeedParams } from './supportQueryHelpers';

// Realtime invalidation is primary; polling covers unavailable WebSocket connections.
const NOTIFICATIONS_POLL_INTERVAL_MS = 30_000;

export function useSupportNotificationsQuery(params: SupportNotificationFeedParams, options?: { enabled?: boolean }) {
	const userSession = useUserSession();
	return useQuery({
		queryKey: SUPPORT_QUERY_KEYS.notifications.feed(params),
		staleTime: 15_000,
		retry: supportQueryRetry,
		queryFn: () =>
			userSession.webServiceHelper.callAsync<IPagedCollection<ISupportNotification>>(
				SUPPORT_PATHS.notifications.feed(params),
				'GET'
			),
		enabled: options?.enabled ?? true,
		meta: { toastErrorMessage: 'Failed to load notifications' },
	});
}

export function useSupportNotificationsUnreadCountQuery(options?: { enabled?: boolean }) {
	const userSession = useUserSession();
	return useQuery({
		queryKey: SUPPORT_QUERY_KEYS.notifications.unreadCount(),
		staleTime: 15_000,
		refetchInterval: NOTIFICATIONS_POLL_INTERVAL_MS,
		refetchIntervalInBackground: false,
		refetchOnWindowFocus: true,
		retry: supportPollingRetry,
		queryFn: () => userSession.webServiceHelper.callAsync<number>(SUPPORT_PATHS.notifications.unreadCount, 'GET'),
		enabled: options?.enabled ?? true,
	});
}

function useInvalidateNotifications() {
	const queryClient = useQueryClient();
	return () => queryClient.invalidateQueries({ queryKey: SUPPORT_QUERY_KEYS.notifications.all() });
}

export function useMarkNotificationReadMutation() {
	const userSession = useUserSession();
	const invalidate = useInvalidateNotifications();
	return useMutation({
		mutationFn: (notificationId: string) =>
			userSession.webServiceHelper.callAsync(SUPPORT_PATHS.notifications.read(notificationId), 'PUT'),
		onSettled: invalidate,
	});
}

export function useMarkNotificationsReadMutation() {
	const userSession = useUserSession();
	const invalidate = useInvalidateNotifications();
	return useMutation({
		mutationFn: (ticketId?: string) =>
			userSession.webServiceHelper.callAsync(SUPPORT_PATHS.notifications.readBatch(ticketId), 'PUT'),
		onSettled: invalidate,
	});
}
